import { isIneligibleLeadZip } from './zips/isIneligibleLeadZip';

type ZipRange = number[];
type SingleRange = [ZipRange, number[]];
type ZipRangesType = SingleRange[];

export type ZipDataType = {
  utilities: string[];
  data: ZipRangesType;
};

/**
 * Binary search tree to find utility company name by zip
 */
export const findUtilityByZip = (
  zipData: ZipDataType,
  zip: number
): string[] | null => {
  const { utilities, data } = zipData;
  const utilityKeys = binarySearchForUtilKeys(data, zip);
  if (utilityKeys === null) return null;
  return utilityKeys.map((key) => utilities[key]);
};

const isZipInRange = (range: ZipRange, zip: number): boolean => {
  const startZip = range[0];
  const endZip = range?.[1];
  if (range.length === 1) {
    return startZip === zip;
  } else {
    const isZipWithinRange = startZip < zip && endZip > zip;
    const isZipAtEndOfRange = startZip === zip || endZip === zip;

    return isZipWithinRange || isZipAtEndOfRange;
  }
};

const isZipGreaterThanRange = (range: ZipRange, zip: number): boolean => {
  const endZipIndex = range.length - 1;
  return zip > range[endZipIndex];
};

const binarySearchForUtilKeys = (
  arr: ZipRangesType,
  zip: number
): number[] | null => {
  let start = 0;
  let end = arr.length - 1;

  while (start <= end) {
    const mid = Math.floor((start + end) / 2);
    const zipRange = arr[mid][0]; // either single zip or a [start, end] array
    const utilKey = arr[mid][1];
    if (isZipInRange(zipRange, zip)) {
      return utilKey; // return the utility key
    }
    // Else look in left or
    // right half accordingly
    else if (isZipGreaterThanRange(zipRange, zip)) {
      start = mid + 1; // array mid has to the lower number in index
    } else {
      end = mid - 1;
    }
  }
  return null;
};

export const isActiveZip = (zipData: ZipDataType, zip: number): boolean => {
  const { data } = zipData;
  const zipHasEligibleUtility = !!binarySearchForUtilKeys(data, zip);
  const isEligibleZip = !isIneligibleLeadZip(zip);
  return zipHasEligibleUtility && isEligibleZip;
};
